import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  InputProps,
  Modal,
  Space,
  TablePaginationConfig,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import {
  ReactNode,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useMutation } from '@tanstack/react-query';
import { IParams } from 'apis/GraphQL/Variables/hoPlan';
import HOPlanApi from 'apis/HOPlanApis';
import { PlantApi } from 'apis/PlantApis';
import { DownloadIcon, EditIcon } from 'assets/svg';
import { SelectForm } from 'components';
import ButtonAction from 'components/Button/ButtonAction';
import TableComponent from 'components/Table/TableComponent';
import useQueryParam from 'hooks/useQueryPrams';
import { IPlant } from 'interfaces/Plant/Plant';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { HocChangePagination } from 'utils/HocChangePagination';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import formatNumber, {
  formatCurrency,
  formatDate,
  toastError,
  total,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IData, IHOTablePlan } from './HOPlan';
import { useUser } from 'contexts/UserProvider';
import { toast } from 'react-toastify';

const { confirm } = Modal;

export enum IModules {
  EDIT = 'EDIT',
  DETAIL = 'DETAIL',
  DELETE = 'DELETE',
  DOWNLOAD = 'DOWNLOAD',
}

interface IProps {
  data: IData;
  plants: IPlant[];
  loading?: boolean;
  modules?: IModules[];
  initData: IHOTablePlan;
  selectedIndex?: number;
  pagination?: TablePaginationConfig;
  handleDelete: any;
  isAddRow: boolean;
  onCancel: () => void;
  setSelectedIndex: (id: number) => void;
  onRowChange: (record: any, newValue: any) => void;
  getHOPlants: (params: any) => void;
}

interface IRenderTable {
  message?: string;
  inputProps: InputProps | any;
  component?: ReactNode;
  customValidate?: () => Promise<void>;
}

type INewPlants = IPlant & { disabled: boolean };

const { SaveTwoTone, CloseCircleTwoTone } = icons;

const monthData = Array.from({ length: 12 }, (_, index) => ({
  name: `${dayjs().month(index).format('MMM').toLowerCase()}Saleamt`,
  displayName: `tháng ${index + 1}`,
}));

const TableHOPLan = memo(
  forwardRef(
    (
      {
        data,
        plants,
        initData,
        loading,
        modules = [
          IModules.EDIT,
          IModules.DETAIL,
          IModules.DELETE,
          IModules.DOWNLOAD,
        ],
        selectedIndex,
        setSelectedIndex,
        onRowChange,
        onCancel,
        getHOPlants,
        isAddRow,
      }: IProps,
      ref: any,
    ) => {
      const [form] = Form.useForm();
      const { user } = useUser();
      const [newPlants, setNewPlants] = useState<INewPlants[]>([]),
        [previousYear, setPreviousYear] = useState<string>(),
        [previousPlantId, setPreviousPlantId] = useState<string>(),
        [previousTypeRevenue, setPreviousTypeRevenue] = useState<string>();

      const [typeRevenue, setTypeRevenue] = useState([{}]);

      const queryParam = useQueryParam(),
        page = parseInt(queryParam.get('page') + '') || 1,
        page_size =
          parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

      const errors: any = [];

      useImperativeHandle(ref, () => ({ handleExportExcel, getData }));

      const { mutate: getTypeRevenue } = useMutation({
        mutationFn: () => PlantApi.getTypeRevenueComboBox(),
        onSuccess: (res: any) => {
          setTypeRevenue(res?.data?.getCode?.items);
        },
        onError: (error) => {
          return console.log(error);
        },
      });

      useEffect(() => {
        setPreviousYear(undefined);
        setPreviousPlantId(undefined);
        setPreviousTypeRevenue(undefined);
        getTypeRevenue();
      }, []);

      useEffect(() => {
        const newPlants = plants.map((plant) => ({
          ...plant,
          disabled: false,
        }));

        setNewPlants(newPlants);
      }, [plants]);

      const getData = () => ({ headerRow: columns, datas: data });

      const { isPending: checkExistedLoading, mutateAsync: checkExistedPlant } =
        useMutation({
          mutationFn: (
            params: Pick<IParams, 'year' | 'plantId' | 'typeRevenue'>,
          ) => HOPlanApi.checkExisted(params),
          onError: (error) => {
            console.log(error);
          },
        });

      const renderTable = ({
        message = '',
        inputProps,
        component,
        customValidate,
      }: IRenderTable) => {
        const { name, defaultValue, value } = inputProps;
        const initValue =
          value !== undefined && value !== null
            ? value
            : (defaultValue ?? (initData as any)[name] ?? '');

        const nonNegativeNames = [
          'totalYearSaleamt',
          ...monthData.map((month) =>
            month.name.replace('Saleamt', 'ProfitSaleamt'),
          ),
        ];

        const validate = async (_: any, value: string) => {
          const numericValue = Number(value);
          const { typeRevenue } = form.getFieldsValue();

          if (name === 'unitMoney') {
            return Promise.resolve();
          }

          if (value === '' || (name === 'year' && !value)) {
            errors.push(message);
            return Promise.reject();
          }

          const isAllowedNegative =
            typeRevenue === 'DT03' || typeRevenue === 'DT04';

          if (
            !nonNegativeNames.includes(name) &&
            !isAllowedNegative &&
            numericValue < 0
          ) {
            const month = monthData.find((month) => month.name === name);
            const errorMessage =
              month && `Giá trị ${month.displayName} không được âm`;
            errors.push(errorMessage);
            return Promise.reject();
          }

          if (customValidate) {
            try {
              await customValidate();
            } catch (error) {
              errors.push(error);
              return Promise.reject(error);
            }
          }

          return Promise.resolve();
        };

        const getMonthName = (fieldName: string): string => {
          const months = {
            janSaleamt: 'Tháng 1',
            febSaleamt: 'Tháng 2',
            marSaleamt: 'Tháng 3',
            aprSaleamt: 'Tháng 4',
            maySaleamt: 'Tháng 5',
            junSaleamt: 'Tháng 6',
            julSaleamt: 'Tháng 7',
            augSaleamt: 'Tháng 8',
            sepSaleamt: 'Tháng 9',
            octSaleamt: 'Tháng 10',
            novSaleamt: 'Tháng 11',
            decSaleamt: 'Tháng 12',
          };
          return months[fieldName as keyof typeof months] || fieldName;
        };

        const handleOnChange = (value: any) => {
          const data = form.getFieldsValue();
          const newValue = value.target.value;

          const valueFormatted = newValue.replace(/\./g, '').replace(',', '.');

          if (
            valueFormatted !== '' &&
            !/^-?$|^-?(?:\d{1,3}(?:\.\d{3})*|\d+)(,\d+)?$/.test(valueFormatted)
          ) {
            form.setFields([{ name: name, errors: [''] }]);
            toast.dismiss();

            return toastError(`Giá trị ${getMonthName(name)} chỉ được nhập số`);
          }

          const numericValue = Number(valueFormatted);

          if (!isNaN(numericValue)) {
            form.setFieldsValue({ [name]: formatNumber(numericValue) });
          } else {
            form.setFieldsValue({ [name]: undefined });
          }

          if (newValue === '') {
            form.setFieldsValue({ [name]: '' });
          }

          const totals = Object.entries(data).reduce(
            (acc, [key, value]) => {
              const numericVal =
                typeof value === 'string'
                  ? parseFloat(value.replace(/\./g, '').replace(',', '.'))
                  : value;

              if (typeof numericVal === 'number' && !isNaN(numericVal)) {
                if (key.includes('Saleamt') && key.length === 10) {
                  acc.totalYearSaleamt += numericVal;
                } else if (key.includes('ProfitSaleamt')) {
                  acc.grossProfitPlanAmt += numericVal;
                }
              }
              return acc;
            },
            { totalYearSaleamt: 0, grossProfitPlanAmt: 0 },
          );

          form.setFieldsValue({
            totalYearSaleamt: formatNumber(totals.totalYearSaleamt),
            grossProfitPlanAmt: totals.grossProfitPlanAmt,
          });
        };

        return (
          <Form.Item
            name={name}
            className="m-0"
            initialValue={initValue}
            rules={[
              { validator: validate },
              ...(name.includes('Saleamt')
                ? [
                    {
                      pattern: /^-?\d{1,3}(\.\d{3})*(,\d+)?$|^-?\d+$/,
                      message: '',
                    },
                  ]
                : []),
            ]}
          >
            {component ? (
              component
            ) : (
              <Input {...inputProps} onChange={handleOnChange} />
            )}
          </Form.Item>
        );
      };

      const renderActionButton = (record: any, index: number) => {
        const isEdit = Number(record.year) >= new Date().getFullYear();
        const classes = isEdit
          ? 'cursor-pointer'
          : 'opacity-50 pointer-events-none';

        return (
          <Space size={'middle'} className="text-lg ">
            {/* {modules.includes(IModules.DETAIL) && (
              <Tooltip placement="top" title={'Xem chi tiết'}>
                <EyeIcon
                  className="cursor-pointer"
                  onClick={() => {
                    if (selectedIndex !== undefined) return;

                    navigate(`${path.HO_PLAN_DETAIL}`, { state: record });
                  }}
                />
              </Tooltip>
            )} */}
            {modules.includes(IModules.EDIT) &&
              (user?.isAdmin || user?.isHO) && (
                <Tooltip placement="top" title={'Chỉnh sửa'}>
                  <EditIcon
                    className={classes}
                    onClick={() => {
                      if (selectedIndex !== undefined) return;

                      const year = dayjs(record.year.toString(), 'YYYY');

                      const newItem: any = { ...record };
                      const keys = Object.keys(newItem);

                      keys.map((key) => {
                        if (
                          (key.endsWith('Saleamt') ||
                            key.endsWith('PlanAmt')) &&
                          newItem[key]
                        ) {
                          newItem[key] = newItem[key]
                            ? formatNumber(newItem[key])
                            : null;
                        }
                      });

                      newItem['year'] = year;
                      form.setFieldsValue(newItem);
                      form.setFieldValue('year', year);
                      setSelectedIndex(index);
                      setPreviousPlantId(newItem['plantId']);
                      setPreviousTypeRevenue(newItem['typeRevenue']);
                      setPreviousYear(year.year().toString());
                    }}
                  />
                </Tooltip>
              )}

            {modules.includes(IModules.DOWNLOAD) && (
              <Tooltip placement="top" title={'Tải xuống'}>
                <DownloadIcon
                  className="cursor-pointer"
                  onClick={() => {
                    if (selectedIndex !== undefined) return;

                    exportExcel_v2(
                      columns,
                      [data.items[index]],
                      'Revenue_Plan_HO',
                    );
                  }}
                />
              </Tooltip>
            )}
            {/* {modules.includes(IModules.DELETE) && (
              <Tooltip placement="top" title={'Xóa kế hoạch'}>
                <XIcon
                  className="cursor-pointer"
                  onClick={() => {
                    if (selectedIndex !== undefined) return;

                    confirm({
                      okText: 'Xác nhận',
                      cancelText: 'Huỷ',
                      title: `Bạn có muốn xoá kế hoạch năm ${record.year} của chi nhánh ${record.tplant.name}?`,
                      onOk() {
                        handleDelete(record.id);
                      },
                    });
                  }}
                />
              </Tooltip>
            )} */}
          </Space>
        );
      };

      const unit = 1;

      const createMonthColumn = (
        monthIndex: number,
        monthName: string,
        unit: number,
      ): ColumnsTypeCustom[number] => ({
        type: 'number',
        width: 150,
        title: `Tháng ${monthIndex}`,
        align: 'right',
        dataIndex: `${monthName}`,
        className: 'align-top',
        currency: 'vi-VN',
        unit: unit,
        render(value: any, _: any, index: number) {
          return index === selectedIndex
            ? renderTable({
                inputProps: {
                  value: value,
                  className: 'w-full',
                  name: `${monthName}`,
                },
                message: `Nhập doanh thu tháng ${monthIndex}`,
              })
            : formatCurrency({ value, round: true });
        },
      });

      const monthColumns = monthData?.map((month, index) =>
        createMonthColumn(index + 1, month.name, unit),
      );

      const columns: ColumnsTypeCustom = [
        {
          type: 'number',
          width: 50,
          title: 'STT',
          align: 'center',
          className: 'align-top',
          render: (_, __, index) => index + 1 + page_size * (page - 1),
        },
        {
          width: 150,
          title: 'Chi nhánh',
          dataIndex: 'plantId',
          align: 'center',
          className: 'align-top',
          render(value, _, index) {
            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    value: value,
                    name: 'plantId',
                  },
                  message: 'Chọn chi nhánh',
                  // customValidate: checkExistedValidate,
                  component: (
                    <SelectForm
                      value={value}
                      size="middle"
                      allowClear={false}
                      disabled={!isAddRow && selectedIndex !== undefined}
                      className="w-full"
                      onChange={handleChangeOption}
                      options={newPlants.map((plant) => ({
                        label: plant.id,
                        value: plant.name,
                      }))}
                    />
                  ),
                })
              : value;
          },
        },
        {
          width: 150,
          title: 'Tên chi nhánh',
          dataIndex: 'tplantName',
          align: 'left',
          className: 'align-top',
          render(value, _, index) {
            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    value: value,
                    disabled: true,
                    type: 'text',
                    name: 'tplantName',
                  },
                  message: 'Nhập tên chi nhánh',
                  component: (
                    <Input
                      value={value}
                      name="tplantName"
                      disabled
                      className="w-full"
                    />
                  ),
                })
              : value;
          },
        },
        {
          width: 100,
          title: 'Năm',
          type: 'number',
          align: 'center',
          dataIndex: 'year',
          className: 'align-top',
          render(value, _, index) {
            const currentYear =
              typeof value === 'object' ||
              typeof value === 'string' ||
              typeof value === 'number'
                ? dayjs(value, 'YYYY').year().toString()
                : value.toString();

            const disabledDate = (currentDate: dayjs.Dayjs) => {
              return (
                dayjs(currentDate, 'YYYY').year() < new Date().getFullYear()
              );
            };

            return index === selectedIndex
              ? renderTable({
                  message: 'Nhập năm',
                  inputProps: {
                    name: 'year',
                  },
                  component: (
                    <DatePicker
                      picker="year"
                      inputReadOnly
                      disabled={!isAddRow && selectedIndex !== undefined}
                      placeholder="Chọn năm"
                      value={currentYear}
                      disabledDate={disabledDate}
                    />
                  ),
                })
              : value || '-';
          },
        },
        {
          width: 150,
          title: 'Mã loại doanh thu',
          dataIndex: 'typeRevenue',
          align: 'left',
          className: 'align-top',
          render(value, _, index) {
            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    value: value,
                    readOnly: true,
                    type: 'text',
                    name: 'typeRevenue',
                  },
                  message: 'Chọn mã loại doanh thu',
                  component: (
                    <SelectForm
                      value={value}
                      allowClear={false}
                      size="middle"
                      className="w-full"
                      disabled={!isAddRow && selectedIndex !== undefined}
                      onChange={handleChangeTypeRevenue}
                      options={typeRevenue.map((type: any) => ({
                        label: type.id,
                        value: type.id,
                        labelName: type.name,
                        labelValue: type.value,
                      }))}
                    />
                  ),
                })
              : value || '-';
          },
        },
        {
          width: 150,
          title: 'Loại doanh thu',
          dataIndex: ['ttypeRevenue', 'name'],
          align: 'left',
          className: 'align-top',
          render(value, _, index) {
            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    value: value,
                    readOnly: true,
                    type: 'text',
                    name: ['ttypeRevenue', 'name'],
                  },
                  message: 'Nhập tên loại doanh thu',
                  component: (
                    <Input
                      value={value}
                      disabled
                      name="ttypeRevenue"
                      readOnly={true}
                      className="w-full"
                    />
                  ),
                })
              : value || '-';
          },
        },
        {
          width: 100,
          title: 'Giá trị',
          dataIndex: ['ttypeRevenue', 'value'],
          align: 'left',
          className: 'align-top',
          render(value, _, index) {
            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    value: value,
                    readOnly: true,
                    type: 'text',
                    name: ['ttypeRevenue', 'value'],
                  },
                  message: 'Nhập tên loại doanh thu',
                  component: (
                    <Input
                      value={value}
                      disabled
                      name="ttypeRevenue"
                      readOnly={true}
                      className="w-full"
                    />
                  ),
                })
              : value || '-';
          },
        },
        {
          width: 200,
          title: 'Tổng năm',
          dataIndex: 'totalYearSaleamt',
          className: 'align-top',
          type: 'number',
          align: 'right',
          currency: 'vi-VN',
          unit: unit,
          render(value, _, index) {
            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    unit: unit,
                    value: value,
                    type: 'text',
                    disabled: true,
                    className: 'w-full',
                    name: 'totalYearSaleamt',
                  },
                })
              : formatCurrency({ value, round: true });
          },
        },
        ...monthColumns,
        {
          width: 100,
          title: 'Tiền tệ',
          dataIndex: 'unitMoney',
          align: 'left',
          className: 'align-top',
          render(value, record, index) {
            if (record.ttypeRevenue.value === '%') {
              return '-';
            }

            return index === selectedIndex
              ? renderTable({
                  inputProps: {
                    value: value,
                    readOnly: true,
                    type: 'text',
                    name: 'unitMoney',
                  },
                  component: (
                    <Input
                      value={value}
                      disabled
                      name="unitMoney"
                      readOnly={true}
                      className="w-full"
                    />
                  ),
                })
              : value;
          },
        },
        {
          width: 150,
          title: 'Trạng thái',
          dataIndex: 'status',
          align: 'left',
          className: 'align-top',
          render: (value) => value || '-',
        },
        {
          width: 150,
          title: 'Người gửi duyệt',
          dataIndex: 'whoApproval',
          align: 'left',
          className: 'align-top',
          render: (value) => value || '-',
        },
        {
          width: 150,
          title: 'Ngày gửi duyệt',
          dataIndex: 'dayApproval',
          align: 'left',
          className: 'align-top',
          render: (value) => value || '-',
        },
        {
          width: 150,
          title: 'Người duyệt',
          dataIndex: 'whoApproval',
          align: 'left',
          className: 'align-top',
          render: (value) => value || '-',
        },
        {
          width: 150,
          title: 'Người tạo',
          dataIndex: 'tcreateUser',
          align: 'left',
          className: 'align-top',
          render: (value) => value || '-',
        },
        {
          width: 210,
          title: 'Ngày tạo',
          dataIndex: 'createDate',
          type: 'date',
          align: 'left',
          className: 'align-top',
          render: (value) =>
            dayjs(value).isValid()
              ? formatDate(value, 'DD/MM/YYYY HH:mm:ss')
              : value,
        },
        {
          width: 150,
          title: 'Người cập nhật',
          dataIndex: 'tmodifyUser',
          align: 'left',
          className: 'align-top',
          render: (value) => value || '-',
        },
        {
          width: 210,
          title: 'Ngày cập nhật',
          dataIndex: 'modifyDate',
          align: 'left',
          className: 'align-top',
          type: 'date',
          render: (value) =>
            dayjs(value).isValid()
              ? formatDate(value, 'DD/MM/YYYY HH:mm:ss')
              : value,
        },
        {
          width: 150,
          title: 'Thao tác',
          fixed: 'right',
          align: 'center',
          render: (_, record, index) => {
            return selectedIndex === index ? (
              <Space size={'middle'} className="text-lg ">
                <ButtonAction tooltip="Lưu" onClick={() => handleSave(record)}>
                  <SaveTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
                <ButtonAction tooltip="Hủy" onClick={handleCancel}>
                  <CloseCircleTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
              </Space>
            ) : (
              renderActionButton(record, index)
            );
          },
        },
      ];

      const collectErrors = async (form: any) => {
        errors.length = 0;
        try {
          await form.validateFields();
          return null;
        } catch (errorInfo: unknown) {
          if (
            typeof errorInfo === 'object' &&
            errorInfo !== null &&
            'errorFields' in errorInfo &&
            Array.isArray((errorInfo as any).errorFields)
          ) {
            if (errors.length > 0 && errors.at(-1) !== undefined) {
              toastError(errors[0]);
            }
            return 'Có lỗi xảy ra khi xác thực biểu mẫu';
          }
          return 'Có lỗi xảy ra khi xác thực biểu mẫu';
        }
      };

      const checkFormValidate = async () => {
        const errors = await collectErrors(form);
        if (errors) {
          return false;
        }
        return true;
      };

      const handleSave = async (record: any) => {
        if (!(await checkFormValidate())) {
          form.validateFields();
          return;
        }

        const fields = form.getFieldsValue();

        Object.keys(fields).forEach((key) => {
          if (key.includes('Saleamt')) {
            const valueWithoutDots = fields[key].toString().replace(/\./g, '');
            fields[key] = parseFloat(valueWithoutDots);
          }
        });

        const { totalOfAll } = total(fields);

        const CheckExisted = await Promise.all([
          checkExistedPlant({
            plantId: fields.plantId,
            typeRevenue: fields.typeRevenue,
            year: parseInt(fields.year.year()),
          }),
        ]);

        const isExisted = CheckExisted[0].data.getRevenueHo.items.length > 0;
        const existingData = CheckExisted[0].data.getRevenueHo.items;

        if (isExisted && isAddRow) {
          return confirm({
            okText: 'Xác nhận',
            cancelText: 'Huỷ',
            title: `Kế hoạch năm ${existingData[0].year} của chi nhánh ${existingData[0].tplant?.name} đã tồn tại. Bạn có muốn cập nhật lại kế hoạch này không?`,
            onOk() {
              monthData.forEach((month) => {
                const monthValue = Number(fields[month.name]);
                if (
                  (fields.typeRevenue === 'DT01' ||
                    fields.typeRevenue === 'DT02') &&
                  monthValue < 0
                ) {
                  errors.push(`Giá trị ${month.displayName} không được âm.`);
                }
              });
              if (errors[0] === undefined) {
                onRowChange(existingData[0], {
                  ...fields,
                  year: dayjs(fields.year).year(),
                });
              } else {
                toastError(errors[0]);
              }
            },
          });
        }

        fields['year'] = fields.year.year();
        fields['totalYearSaleamt'] = totalOfAll;

        const newValue =
          typeof selectedIndex !== 'undefined'
            ? { ...data.items[selectedIndex], ...fields }
            : fields;

        form.resetFields();
        onRowChange(record, newValue);

        setPreviousYear(undefined);
        setPreviousPlantId(undefined);
        setPreviousTypeRevenue(undefined);
      };

      const handleCancel = () => {
        form.resetFields();
        form.setFieldsValue({ ...initData, ttypeRevenue: '' });

        setPreviousYear(undefined);
        setPreviousPlantId(undefined);
        setPreviousTypeRevenue(undefined);

        onCancel();
      };

      const handleExportExcel = () => {
        getHOPlants({
          page: 1,
          pageSize: data.pageInfo?.totalItems,
          type: 'export',
          headerRow: columns,
        });
      };

      const handleChangeOption = (_: any, option: any) => {
        const fieldValues = form.getFieldsValue();

        form.setFieldsValue({
          ...fieldValues,
          plantId: option.label,
          tplantName: option.value,
        });
      };

      const handleChangeTypeRevenue = (_: any, option: any) => {
        const fieldValues = form.getFieldsValue();

        form.setFieldsValue({
          ...fieldValues,
          typeRevenue: option.label,
          ttypeRevenue: { name: option.labelName, value: option.labelValue },
        });
      };

      return (
        <>
          <Form form={form}>
            <TableComponent
              columns={columns}
              dataSource={data.items}
              loading={loading || checkExistedLoading}
              pagination={{
                disabled: selectedIndex === 0,
                current: data.pageInfo?.currentPage,
                pageSize: data.pageInfo?.itemsPerPage,
                total: data.pageInfo?.totalItems,
                onChange: HocChangePagination(),
              }}
            />
          </Form>
        </>
      );
    },
  ),
);

export default TableHOPLan;
